import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Loading } from "@/components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { backOrigin, useBackTo } from "@/hooks/useBackTo";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";
import { ApiError } from "@/lib/apiClient";
import { toastApiError } from "@/lib/toast";
import { products as productsApi, salesCampaigns as campaignsApi, taxRates as taxRatesApi, type ProductDetail, type ProductListItem, type SalesCampaignListItem, type TaxRateRow } from "@/features/catalog/api/catalog.api";
import { quotes as quotesApi, type SaveQuoteRequest } from "@/features/quotes/api/quotes.api";
import { computeQuoteTotals, formatMoney } from "@/utils/quoteTotals";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Form } from "@/components/ui/form";
import { TextField, TextAreaField, NumberField, NumberCell, CheckField, useAutoFocus, useUnsavedChangesWarning } from "@/components/form";
import { Notice } from "@/components/Notice";

const NO_CAMPAIGN = "__none__";
const FREE_FORM = "__free__";
const CUSTOM_TAX = "__custom__";

const nonNegAmount = z.string().refine((s) => s === "" || (Number.isFinite(Number(s)) && Number(s) >= 0), "Must be ≥ 0.");
const wholeQty = z.string().refine((s) => s === "" || (Number.isInteger(Number(s)) && Number(s) >= 0), "Must be a whole number ≥ 0.");

const itemSchema = z.object({
  productId: z.string().nullable(),
  description: z.string().max(FIELD_MAX.quote.itemDescription, tooLong("Item description", FIELD_MAX.quote.itemDescription)),
  unitPrice: nonNegAmount,
  quantity: wholeQty,
});

const num = z.number().nullable();
const pct = z.number().min(0, "Value cannot be negative.").max(100, "Value cannot exceed 100.").nullable();

const schema = z.object({
  customerName: z.string().trim().min(1, "Customer name is required.").max(FIELD_MAX.quote.customerName, tooLong("Customer name", FIELD_MAX.quote.customerName)),
  customerCompany: z.string().max(FIELD_MAX.quote.customerCompany, tooLong("Customer company", FIELD_MAX.quote.customerCompany)),
  customerEmail: z.string().max(FIELD_MAX.quote.customerEmail, tooLong("Customer email", FIELD_MAX.quote.customerEmail)),
  customerPhone: z.string().max(FIELD_MAX.quote.customerPhone, tooLong("Customer phone", FIELD_MAX.quote.customerPhone)),
  customerAddress: z.string().max(FIELD_MAX.quote.customerAddress, tooLong("Customer address", FIELD_MAX.quote.customerAddress)),
  shippingName: z.string().max(FIELD_MAX.quote.shippingName, tooLong("Shipping name", FIELD_MAX.quote.shippingName)),
  shippingCompany: z.string().max(FIELD_MAX.quote.shippingCompany, tooLong("Shipping company", FIELD_MAX.quote.shippingCompany)),
  shippingEmail: z.string().max(FIELD_MAX.quote.shippingEmail, tooLong("Shipping email", FIELD_MAX.quote.shippingEmail)),
  shippingPhone: z.string().max(FIELD_MAX.quote.shippingPhone, tooLong("Shipping phone", FIELD_MAX.quote.shippingPhone)),
  shippingAddress: z.string().max(FIELD_MAX.quote.shippingAddress, tooLong("Shipping address", FIELD_MAX.quote.shippingAddress)),
  campaignId: z.string().nullable(),
  items: z.array(itemSchema),
  discountPct: pct,
  discountAmount: num,
  taxPct: pct,
  shippingCharge: num,
  discountAfterTax: z.boolean(),
  notes: z.string().max(FIELD_MAX.quote.notes, tooLong("Notes", FIELD_MAX.quote.notes)),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_ITEM: FormValues["items"][number] = { productId: null, description: "", unitPrice: "", quantity: "1" };

function blankValues(): FormValues {
  return {
    customerName: "",
    customerCompany: "",
    customerEmail: "",
    customerPhone: "",
    customerAddress: "",
    shippingName: "",
    shippingCompany: "",
    shippingEmail: "",
    shippingPhone: "",
    shippingAddress: "",
    campaignId: null,
    items: [{ ...EMPTY_ITEM }],
    discountPct: 0,
    discountAmount: 0,
    taxPct: 0,
    shippingCharge: 0,
    discountAfterTax: false,
    notes: "",
  };
}

export default function QuoteEditPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const backTo = useBackTo("/quotes");

  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [campaigns, setCampaigns] = useState<SalesCampaignListItem[]>([]);
  const [allProducts, setAllProducts] = useState<ProductListItem[]>([]);
  const [allTaxRates, setAllTaxRates] = useState<TaxRateRow[]>([]);

  const [taxTouched, setTaxTouched] = useState(false);

  const [priceLevelSnap, setPriceLevelSnap] = useState<number | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: blankValues(),
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: "items" });

  useAutoFocus(form, "customerName", !isEdit);

  const campaignId = form.watch("campaignId");
  const wItems = form.watch("items");
  const wDiscountPct = form.watch("discountPct");
  const wDiscountAmount = form.watch("discountAmount");
  const wTaxPct = form.watch("taxPct");
  const wShipping = form.watch("shippingCharge");
  const wAfterTax = form.watch("discountAfterTax");

  const activePriceLevel = useMemo(() => {
    if (priceLevelSnap != null) return priceLevelSnap;
    if (!campaignId) return 1;
    const c = campaigns.find((x) => x.id === campaignId);
    return c?.priceLevel ?? 1;
  }, [priceLevelSnap, campaignId, campaigns]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      campaignsApi.listAll().catch(() => [] as SalesCampaignListItem[]),
      productsApi.listAll().catch(() => [] as ProductListItem[]),
      taxRatesApi.list().catch(() => [] as TaxRateRow[]),
    ]).then(([cs, ps, trs]) => {
      if (cancelled) return;
      setCampaigns(cs.filter((c) => c.status === "Active"));
      setAllProducts(ps);
      setAllTaxRates(trs);

      if (!isEdit && !taxTouched) {
        const def = trs.find((t) => t.isDefault);
        if (def) form.setValue("taxPct", def.rate * 100);
      }
    });
    return () => { cancelled = true; };

  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    quotesApi.get(id)
      .then((q) => {
        if (cancelled) return;
        form.reset({
          customerName: q.customerName,
          customerCompany: q.customerCompany ?? "",
          customerEmail: q.customerEmail ?? "",
          customerPhone: q.customerPhone ?? "",
          customerAddress: q.customerAddress ?? "",
          shippingName: q.shippingName ?? "",
          shippingCompany: q.shippingCompany ?? "",
          shippingEmail: q.shippingEmail ?? "",
          shippingPhone: q.shippingPhone ?? "",
          shippingAddress: q.shippingAddress ?? "",
          campaignId: q.campaignId,
          items:
            q.items.length > 0
              ? q.items.map((it) => ({
                  productId: it.productId,
                  description: it.description,
                  unitPrice: String(it.unitPrice),
                  quantity: String(it.quantity),
                }))
              : [{ ...EMPTY_ITEM }],
          discountPct: q.discountPct * 100,
          discountAmount: q.discountAmount,
          taxPct: q.taxRate * 100,
          shippingCharge: q.shippingCharge,
          discountAfterTax: q.discountAppliesAfterTax,
          notes: q.notes ?? "",
        });
        setPriceLevelSnap(q.priceLevelSnap);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof Error ? e.message : "Failed to load the quote.");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, isEdit, form]);

  const totals = useMemo(
    () =>
      computeQuoteTotals(
        wItems.map((it) => ({ unitPrice: toNum(it.unitPrice), quantity: toNum(it.quantity) })),
        (wDiscountPct ?? 0) / 100,
        wDiscountAmount ?? 0,
        (wTaxPct ?? 0) / 100,
        wShipping ?? 0,
        wAfterTax,
      ),
    [wItems, wDiscountPct, wDiscountAmount, wTaxPct, wShipping, wAfterTax],
  );

  function copyCustomerToShipping() {
    const v = form.getValues();
    form.setValue("shippingName", v.customerName);
    form.setValue("shippingCompany", v.customerCompany);
    form.setValue("shippingEmail", v.customerEmail);
    form.setValue("shippingPhone", v.customerPhone);
    form.setValue("shippingAddress", v.customerAddress);
  }

  async function pickProduct(rowIndex: number, productId: string) {
    if (productId === FREE_FORM) {
      form.setValue(`items.${rowIndex}.productId`, null);
      return;
    }
    try {
      const p: ProductDetail = await productsApi.get(productId);
      const tier = priceForLevel(p, activePriceLevel);
      form.setValue(`items.${rowIndex}.productId`, p.id);
      form.setValue(`items.${rowIndex}.description`, p.name);
      form.setValue(`items.${rowIndex}.unitPrice`, String(tier));
    } catch {

      form.setValue(`items.${rowIndex}.productId`, productId);
    }
  }

  function buildRequest(v: FormValues): SaveQuoteRequest {
    return {
      customerName: v.customerName.trim(),
      customerCompany: v.customerCompany.trim() || null,
      customerEmail: v.customerEmail.trim() || null,
      customerPhone: v.customerPhone.trim() || null,
      customerAddress: v.customerAddress.trim() || null,
      shippingName: v.shippingName.trim() || null,
      shippingCompany: v.shippingCompany.trim() || null,
      shippingEmail: v.shippingEmail.trim() || null,
      shippingPhone: v.shippingPhone.trim() || null,
      shippingAddress: v.shippingAddress.trim() || null,
      discountPct: (v.discountPct ?? 0) / 100,
      discountAmount: v.discountAmount ?? 0,
      taxRate: (v.taxPct ?? 0) / 100,
      shippingCharge: v.shippingCharge ?? 0,
      discountAppliesAfterTax: v.discountAfterTax,
      notes: v.notes.trim() || null,
      campaignId: v.campaignId,
      items: v.items
        .filter((it) => it.description.trim())
        .map((it) => ({
          productId: it.productId,
          description: it.description.trim(),
          unitPrice: toNum(it.unitPrice),
          quantity: Math.max(1, Math.floor(toNum(it.quantity)) || 1),
        })),
    };
  }

  const saveAsRef = useRef<"quote" | "invoice" | undefined>(undefined);

  async function onValid(v: FormValues) {
    if (isEdit && !form.formState.isDirty) {
      toast.info("No changes to save");
      return;
    }
    if (v.items.some((it) => (it.productId !== null || it.unitPrice.trim() !== "") && !it.description.trim())) {
      toast.error("Every line item needs a description.");
      return;
    }
    if (!v.items.some((it) => it.description.trim())) {
      toast.error("Add at least one line item.");
      return;
    }
    setError(null);
    try {
      const req = buildRequest(v);
      if (isEdit && id) {
        await quotesApi.update(id, req);
        navigate(`/quotes/${id}`, { state: backOrigin(backTo) });
      } else {
        const created = await quotesApi.create({ ...req, saveAs: saveAsRef.current });
        toast.success("Quote created");
        navigate(`/quotes/${created.id}`, { state: backOrigin(backTo) });
      }
    } catch (e) {
      toastApiError(e, "Save failed.");
    }
  }

  function onInvalid() {
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus({ preventScroll: true });
    });
  }

  function submit(mode?: "quote" | "invoice") {
    saveAsRef.current = mode;
    void form.handleSubmit(onValid, onInvalid)();
  }

  const campaignLocked = isEdit && priceLevelSnap != null;
  const saving = form.formState.isSubmitting;
  useUnsavedChangesWarning(form.formState.isDirty);

  if (forbidden) {
    return (
      <>
        <Notice tone="warn">You don't have permission to edit quotes.</Notice>
      </>
    );
  }
  if (loading) {
    return (
      <>
        <Loading>Loading quote…</Loading>
      </>
    );
  }

  return (
    <>
      <div className="mb-4">
        <Link
          to={isEdit && id ? `/quotes/${id}` : backTo}
          state={isEdit && id ? backOrigin(backTo) : undefined}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to {isEdit ? "quote" : "quotes"}
        </Link>
      </div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Edit quote" : "New quote"}
        </h1>
      </header>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onValid, onInvalid)} className="space-y-6">
          {error && <Notice tone="error">{error}</Notice>}

          <Panel title="Customer">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <TextField control={form.control} name="customerName" label="Name" required />
              <TextField control={form.control} name="customerCompany" label="Company" />
              <TextField control={form.control} name="customerEmail" label="Email" />
              <TextField control={form.control} name="customerPhone" label="Phone" />
              <TextField control={form.control} name="customerAddress" label="Address" className="md:col-span-2" />
            </div>
          </Panel>

          <Panel
            title="Ship to"
            action={
              <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={copyCustomerToShipping}>
                Copy from customer
              </Button>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <TextField control={form.control} name="shippingName" label="Name" />
              <TextField control={form.control} name="shippingCompany" label="Company" />
              <TextField control={form.control} name="shippingEmail" label="Email" />
              <TextField control={form.control} name="shippingPhone" label="Phone" />
              <TextField control={form.control} name="shippingAddress" label="Address" className="md:col-span-2" />
            </div>
          </Panel>

          {(campaigns.length > 0 || allProducts.length > 0) && (
            <Panel title="Sales campaign">
              <label className="block text-sm font-medium text-slate-700 mb-1">Campaign</label>
              <Controller
                control={form.control}
                name="campaignId"
                render={({ field }) => (
                  <SearchableSelect
                    value={field.value ?? NO_CAMPAIGN}
                    onValueChange={(v) => field.onChange(v === NO_CAMPAIGN ? null : v)}
                    disabled={campaignLocked}
                    className="w-full md:w-96"
                    searchPlaceholder="Search campaigns…"
                    options={[
                      { value: NO_CAMPAIGN, label: "— None (free-form pricing) —" },
                      ...campaigns.map((c) => ({ value: c.id, label: `${c.name} (L${c.priceLevel})` })),
                    ]}
                  />
                )}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Picking a campaign fills in product prices from
                <strong> Price Level {activePriceLevel}</strong>.
                {campaignLocked && (
                  <> The level is frozen on existing quotes — change it by creating a new one.</>
                )}
              </p>
            </Panel>
          )}

          <Panel title="Line items">
            <div className="space-y-2">
              <div className="hidden md:flex gap-2 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <div className="w-48">Product</div>
                <div className="flex-1">Description</div>
                <div className="w-28 text-right">Unit price</div>
                <div className="w-20 text-right">Qty</div>
                <div className="w-24 text-right">Line total</div>
                <div className="w-8" />
              </div>
              {fields.map((f, i) => (
                <div key={f.id} className="flex flex-wrap md:flex-nowrap items-center gap-2">
                  <Controller
                    control={form.control}
                    name={`items.${i}.productId`}
                    render={({ field }) => (
                      <SearchableSelect
                        value={field.value ?? FREE_FORM}
                        onValueChange={(v) => pickProduct(i, v)}
                        className="w-48"
                        contentClassName="w-72"
                        searchPlaceholder="Search products…"
                        options={[
                          { value: FREE_FORM, label: "— Free-form —" },
                          ...allProducts.map((p) => ({ value: p.id, label: p.name })),
                        ]}
                      />
                    )}
                  />
                  <Input
                    {...form.register(`items.${i}.description`)}
                    placeholder="Description"
                    className="flex-1 min-w-[160px]"
                  />
                  <Controller
                    control={form.control}
                    name={`items.${i}.unitPrice`}
                    render={({ field }) => (
                      <NumberCell value={field.value} onChange={field.onChange} placeholder="0.00" ariaLabel="Unit price" className="w-28 text-right" />
                    )}
                  />
                  <Controller
                    control={form.control}
                    name={`items.${i}.quantity`}
                    render={({ field }) => (
                      <NumberCell value={field.value} onChange={field.onChange} integer ariaLabel="Quantity" className="w-20 text-right" />
                    )}
                  />
                  <div className="w-24 text-right text-sm tabular-nums text-slate-600">
                    {formatMoney(toNum(wItems[i]?.unitPrice) * Math.max(1, toNum(wItems[i]?.quantity)))}
                  </div>
                  <button
                    type="button"
                    onClick={() => fields.length > 1 && remove(i)}
                    className="w-8 text-destructive text-lg leading-none disabled:text-slate-300"
                    disabled={fields.length === 1}
                    aria-label="Remove line"
                  >
                    ×
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-1"
                onClick={() => append({ ...EMPTY_ITEM })}
              >
                Add line
              </Button>
            </div>
          </Panel>

          <Panel title="Pricing">
            {allTaxRates.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Tax rate preset</label>
                <SearchableSelect
                  value={matchedTaxRateId(wTaxPct, allTaxRates) ?? CUSTOM_TAX}
                  onValueChange={(v) => {
                    const picked = allTaxRates.find((r) => r.id === v);
                    if (picked) {
                      form.setValue("taxPct", picked.rate * 100);
                      setTaxTouched(true);
                    }
                  }}
                  className="w-full md:w-96"
                  searchPlaceholder="Search tax rates…"
                  options={[
                    { value: CUSTOM_TAX, label: "— Custom rate —" },
                    ...allTaxRates.map((r) => ({
                      value: r.id,
                      label: `${r.name} — ${(r.rate * 100).toFixed(3)}%${r.location ? ` (${r.location})` : ""}${r.isDefault ? " · default" : ""}`,
                    })),
                  ]}
                />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <NumberField control={form.control} name="discountPct" label="Discount" suffix="%" min={0} max={100} />
              <NumberField control={form.control} name="discountAmount" label="Discount" prefix="$" min={0} />
              <Controller
                control={form.control}
                name="taxPct"
                render={({ field }) => (
                  <NumberFieldTouched
                    label="Tax rate"
                    suffix="%"
                    min={0}
                    max={100}
                    value={field.value}
                    onChange={(n) => { field.onChange(n); setTaxTouched(true); }}
                  />
                )}
              />
              <NumberField control={form.control} name="shippingCharge" label="Shipping" prefix="$" min={0} />
            </div>
            <div className="mt-3">
              <CheckField control={form.control} name="discountAfterTax" label="Apply discount after tax" />
            </div>

            <div className="mt-4 border-t border-slate-200 pt-3 space-y-1 text-sm">
              <TotalRow label="Subtotal" value={totals.subtotal} />
              <TotalRow label="Tax" value={totals.taxAmount} />
              <TotalRow label="Total" value={totals.totalAmount} bold />
            </div>
          </Panel>

          <Panel title="Notes">
            <TextAreaField control={form.control} name="notes" label="" rows={3} />
          </Panel>

          <div className="flex items-center justify-end gap-3">
            <Button asChild variant="ghost">
              <Link to={isEdit && id ? `/quotes/${id}` : "/quotes"}>Cancel</Link>
            </Button>
            {isEdit ? (
              <Button type="button" disabled={saving} onClick={() => submit()}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" disabled={saving} onClick={() => submit("quote")}>
                  Save as quote
                </Button>
                <Button type="button" disabled={saving} onClick={() => submit("invoice")}>
                  Save as invoice
                </Button>
              </>
            )}
          </div>
        </form>
      </Form>
    </>
  );
}

function toNum(s: string | undefined): number {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function priceForLevel(p: ProductDetail, level: number): number {
  switch (level) {
    case 2: return p.priceLevel2;
    case 3: return p.priceLevel3;
    case 4: return p.priceLevel4;
    default: return p.priceLevel1;
  }
}

function matchedTaxRateId(currentPct: number | null, rates: TaxRateRow[]): string | null {
  if (currentPct == null || !Number.isFinite(currentPct)) return null;
  const target = currentPct / 100;
  const hit = rates.find((r) => Math.abs(r.rate - target) < 1e-6);
  return hit?.id ?? null;
}

function Panel({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent className="pt-6">{children}</CardContent>
    </Card>
  );
}

function TotalRow({ label, value, bold = false }: { label: string; value: number; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold text-slate-900 text-base" : "text-slate-600"}`}>
      <span>{label}</span>
      <span className="tabular-nums">{formatMoney(value)}</span>
    </div>
  );
}

function NumberFieldTouched({
  label, suffix, prefix, value, onChange, min, max,
}: {
  label: string;
  suffix?: string;
  prefix?: string;
  value: number | null;
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
}) {
  const [raw, setRaw] = useState<string>(value == null ? "" : String(value));
  useEffect(() => {
    setRaw((prev) => {
      const prevNum = prev.trim() === "" ? null : Number(prev);
      if (prevNum === value) return prev;
      return value == null ? "" : String(value);
    });
  }, [value]);
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <div className="flex items-stretch overflow-hidden rounded-md border border-input shadow-sm focus-within:ring-1 focus-within:ring-ring">
        {prefix && <span className="flex items-center border-r border-input bg-muted px-2 text-xs text-muted-foreground">{prefix}</span>}
        <Input
          type="number"
          inputMode="decimal"
          step="any"
          min={min}
          max={max}
          value={raw}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "") {
              setRaw(next);
              onChange(null);
              return;
            }
            const n = Number(next);
            if (Number.isNaN(n)) return;
            if (min !== undefined && n < min) return;
            if (max !== undefined && n > max) return;
            setRaw(next);
            onChange(n);
          }}
          className="min-w-0 flex-1 rounded-none border-0 shadow-none focus-visible:ring-0"
        />
        {suffix && <span className="flex items-center border-l border-input bg-muted px-2 text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}
